import { useEffect } from 'react';

import { createDebugLogger, useLatest } from '@videa/utils';

const debug = createDebugLogger('SilenceDetection');

/** RMS threshold below which audio is considered "silent".
 *  0.01 is extremely quiet — only true silence (hardware mute, no input) triggers this.
 *  Normal background noise in a quiet room is typically 0.02–0.05. */
const SILENCE_THRESHOLD = 0.01;

/** How long continuous silence must persist before entering mute state.
 *  5 seconds filters out natural conversational pauses while still catching
 *  a genuinely dead mic in a reasonable timeframe. */
const SILENCE_DELAY_MS = 5_000;

/** How often to sample audio levels (ms).
 *  500ms is plenty for a 3-second silence window — no need for 60fps RAF. */
const CHECK_INTERVAL_MS = 500;

/**
 * Calculates RMS (root mean square) from time-domain audio data.
 * Values are in 0–255 range from AnalyserNode; we normalize to -1..1.
 */
function calculateRms(dataArray: Uint8Array, length: number): number {
  let sumSquares = 0;
  for (let i = 0; i < length; i++) {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const v = (dataArray[i]! - 128) / 128;
    sumSquares += v * v;
  }
  return Math.sqrt(sumSquares / length);
}

export interface UseSilenceDetectionOptions {
  /** The MediaStream to monitor for silence. */
  stream: MediaStream | null;
  /** Whether detection is active (e.g. only while recording). */
  enabled: boolean;
  /** Called when sustained silence is detected. */
  onSilenceStart: () => void;
  /** Called when audio activity resumes after silence. */
  onSilenceEnd: () => void;
  /**
   * How long (ms) continuous silence must persist before firing onSilenceStart.
   * Defaults to SILENCE_DELAY_MS (5 000 ms).
   *
   * Use 0 for instant feedback (e.g. the pre-recording "start recording" page
   * where you want to show immediately that the mic isn't working).
   */
  silenceDelayMs?: number;
}

/**
 * Monitors a MediaStream for sustained silence using Web Audio API RMS analysis.
 *
 * Catches ALL mute types that `audioTrack.muted` alone misses:
 * - Hardware mute buttons (headset mute switch, laptop mic mute key)
 * - OS-level mute (system tray mute, Windows/macOS sound settings)
 * - Wrong device selected / device producing no audio
 *
 * Also uses `audioTrack.muted` as a fast-path for browser-level mute events
 * (which are detected immediately without the delay).
 *
 * Uses setInterval (not requestAnimationFrame) since silence detection
 * doesn't need 60fps — checking every 500ms is sufficient for a 3-second
 * silence window and avoids duplicating the RAF work that MiniMicVisualizer
 * already does for the audio bars.
 */
export function useSilenceDetection({
  stream,
  enabled,
  onSilenceStart,
  onSilenceEnd,
  silenceDelayMs = SILENCE_DELAY_MS,
}: UseSilenceDetectionOptions): void {
  // useLatest avoids stale closures in the interval callback — the callbacks
  // are captured once when the effect runs but .current always points
  // at the latest version.
  const onSilenceStartRef = useLatest(onSilenceStart);
  const onSilenceEndRef = useLatest(onSilenceEnd);

  useEffect(() => {
    if (!enabled || !stream?.active) {
      return;
    }

    const audioTrack = stream.getAudioTracks()[0];
    if (!audioTrack || audioTrack.readyState !== 'live') {
      return;
    }

    // Capture refs inside the effect so the cleanup function uses the same
    // reference that was active when the effect ran.
    const silenceStartCallback = onSilenceStartRef;
    const silenceEndCallback = onSilenceEndRef;

    let currentMuteState = false;
    let silenceSince: number | null = null;

    // Create audio analysis chain
    const audioContext = new AudioContext();
    const analyser = audioContext.createAnalyser();
    const mediaStream = new MediaStream([audioTrack]);
    const source = audioContext.createMediaStreamSource(mediaStream);
    source.connect(analyser);
    analyser.fftSize = 256;

    const bufferLength = analyser.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);

    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    const checkAudio = () => {
      // Fast path: audioTrack.muted catches browser-level mute events immediately
      const isTrackMuted = audioTrack.muted === true;

      // RMS-based detection: catches hardware mutes, OS mutes, dead devices, etc.
      analyser.getByteTimeDomainData(dataArray);
      const rms = calculateRms(dataArray, bufferLength);
      const isAudioSilent = rms < SILENCE_THRESHOLD;

      // Track when continuous silence started
      if (isAudioSilent || isTrackMuted) {
        if (silenceSince === null) {
          silenceSince = performance.now();
        }
      } else {
        silenceSince = null;
      }

      // Enter mute state if:
      // 1. Browser says track is muted (immediate), OR
      // 2. RMS has been below threshold for silenceDelayMs (sustained silence)
      const shouldBeMuted =
        isTrackMuted ||
        (silenceSince !== null &&
          performance.now() - silenceSince >= silenceDelayMs);

      // Only fire callbacks on state change
      if (shouldBeMuted !== currentMuteState) {
        currentMuteState = shouldBeMuted;
        if (shouldBeMuted) {
          debug.warn(
            `Silence detected: trackMuted=${isTrackMuted}, rms=${rms.toFixed(
              4,
            )}, silentFor=${
              silenceSince ? Math.round(performance.now() - silenceSince) : 0
            }ms`,
          );
          silenceStartCallback.current();
        } else {
          debug.log('Audio activity resumed');
          silenceEndCallback.current();
        }
      }

      // Schedule next check — setTimeout chain guarantees one-at-a-time
      // execution (unlike setInterval which fires on a fixed schedule).
      timeoutId = setTimeout(checkAudio, CHECK_INTERVAL_MS);
    };

    // Kick off the first check
    timeoutId = setTimeout(checkAudio, CHECK_INTERVAL_MS);

    return () => {
      if (timeoutId !== null) {
        clearTimeout(timeoutId);
      }
      // When stream changes, close any open silence state so isSilentAtom
      // doesn't get stuck if the new stream starts un-muted.
      silenceEndCallback.current();
      // Clean up audio analysis resources
      source.disconnect();
      analyser.disconnect();
      audioContext.close().catch(() => {
        // Ignore errors if already closed
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- useLatest refs are stable
  }, [enabled, stream, silenceDelayMs]);
}
